import { useEffect, useState } from 'react';
import Iconify from 'src/components/Iconify';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'src/@/components/ui/card';
import { Label } from 'src/@/components/ui/label';
import { Textarea } from 'src/@/components/ui/textarea';
import { calcularTokens } from 'src/utils/ai.utils';

const LABEL_CLS = 'text-xs font-semibold uppercase tracking-wider text-gray-500';
const INPUT_CLS = 'rounded-xl border-gray-200 focus:border-[#7C3AED] focus:ring-[#7C3AED]/20';

export default function InstrucoesAdicionaisSection({ aiAgent, changeModuleConfig }) {
    const complemento = aiAgent?.config?.module?.realstate?.complemento || {};
    const value = typeof complemento.prompt === 'string' ? complemento.prompt : '';
    const [tokens, setTokens] = useState(0);

    useEffect(() => {
        setTokens(calcularTokens(value));
    }, [value]);

    const updateComplemento = (patch) => {
        changeModuleConfig({
            complemento: {
                ...complemento,
                ...patch,
            },
        });
    };

    return (
        <Card className="rounded-xl border border-gray-100 bg-white">
            <CardHeader>
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-violet-50">
                        <Iconify icon="mdi:format-quote-close" width={20} className="text-violet-600" />
                    </div>
                    <div>
                        <CardTitle>Instruções Adicionais</CardTitle>
                        <CardDescription>
                            Texto livre que complementa o comportamento da Laís sem sobrescrever as regras-base do agente.
                        </CardDescription>
                    </div>
                </div>
            </CardHeader>
            <CardContent className="flex flex-col gap-5">
                <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4">
                    <div className="flex items-start gap-3">
                        <Iconify icon="mdi:alert-circle-outline" width={20} className="text-amber-600 mt-0.5 shrink-0" />
                        <div className="flex flex-col gap-2 text-sm">
                            <p className="font-semibold text-amber-900">Antes de escrever aqui</p>
                            <p className="text-amber-800">
                                Este campo é injetado <strong>depois</strong> das regras-base, guardrails, escopo, políticas
                                e dados estruturados que você configura nas outras abas. Ele <strong>não sobrescreve</strong>{' '}
                                nenhuma dessas regras — serve apenas como reforço de tom, restrições adicionais ou regras
                                específicas que não cabem nos campos estruturados.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <Iconify icon="mdi:check-circle-outline" width={18} className="text-emerald-600" />
                            <p className="text-sm font-semibold text-emerald-900">Use para</p>
                        </div>
                        <ul className="text-xs text-emerald-900/80 space-y-1.5 list-disc pl-5">
                            <li>Reforços de tom (ex: "fale com leve sotaque mineiro")</li>
                            <li>Não chamar o cliente pelo nome em toda mensagem</li>
                            <li>Não repetir/reafirmar o que o cliente disse</li>
                            <li>Restrições específicas de vocabulário ou fraseado</li>
                        </ul>
                    </div>
                    <div className="rounded-xl border border-rose-100 bg-rose-50/40 p-4">
                        <div className="flex items-center gap-2 mb-2">
                            <Iconify icon="mdi:close-circle-outline" width={18} className="text-rose-600" />
                            <p className="text-sm font-semibold text-rose-900">Não use para</p>
                        </div>
                        <ul className="text-xs text-rose-900/80 space-y-1.5 list-disc pl-5">
                            <li>Identidade, endereço, horário → aba <strong>Identificação</strong></li>
                            <li>Setores de transferência (10/11/12...) → aba <strong>Transferência</strong></li>
                            <li>Tópicos fora do escopo (IPTU, IR, RH) → aba <strong>Escopo</strong></li>
                            <li>Apresentação inicial → aba <strong>Tom da Conversa</strong></li>
                            <li>Fluxo de qualificação → aba <strong>Perguntas Essenciais</strong></li>
                            <li>"Digite Sim para confirmar" — quebra o fluxo natural do agente</li>
                        </ul>
                    </div>
                </div>

                <div className="flex flex-col gap-2">
                    <Label className={LABEL_CLS}>Texto complementar</Label>
                    <Textarea
                        value={value}
                        onChange={(e) => updateComplemento({ prompt: e.target.value })}
                        className={`${INPUT_CLS} font-mono text-sm leading-5`}
                        rows={12}
                        placeholder="Ex.: Use sotaque mineiro leve. Não cumprimente o cliente pelo nome em toda mensagem — use o nome no máximo 1 vez por conversa. Não reforce o que o cliente acabou de dizer; siga direto para a próxima pergunta."
                    />
                    <div className="flex justify-between items-center text-xs text-muted-foreground">
                        <span className="italic">
                            Dica: menos é mais. Se passar de 10-15 linhas, provavelmente algo deveria estar nas abas estruturadas.
                        </span>
                        <span>Tokens aprox: {tokens}</span>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
